import { useCartInclusionMutation } from '../hooks/useCartInclusionMutation'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { findTransportConnectionForOption } from '../components/FlightSearchDrawer/flightSearchDrawerParams'
import { buildRefreshedFlightItineraryCommittedStateEntry } from '../hooks/buildSelectedFlightItineraryCommittedStateEntry'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { getCartItemDisplayPrice } from '../utils/cartItemPriceSelection'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { computeCartPanelTotal, isCartPanelItemCheckoutReady, shouldIncludeCartPanelItemPriceEstimate } from '../utils/cartPanelUtils'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { CartProjectionContext } from './CartProjectionContext'
import { useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useItineraryContext } from './ItineraryContext'
import { CartPricingSnapshotRegistration } from './CartPricingSnapshotRegistration'
import { useTurnAuthoritySnapshot, type CartContextSummary } from './TurnAuthoritySnapshotContext'
import { useCartPanelItems } from '../hooks/useCartPanelItems'
import { buildCartItemAssistantPrices } from '../utils/cartItemAssistantPrices'

interface Props {
  isLoggedIn: boolean
  children: ReactNode
}

export function CartProjectionProvider({ isLoggedIn, children }: Props) {
  const [optimisticExclusions, setOptimisticExclusions] = useState<Record<number, boolean>>({})
  const [refreshFailedFlightKeys, setRefreshFailedFlightKeys] = useState<Record<string, boolean>>({})
  const reset = useCallback(() => {
    setOptimisticExclusions({})
    setRefreshFailedFlightKeys({})
  }, [])
  useEventSubscription('reset_chat_state', reset)
  const snapshotRef = useTurnAuthoritySnapshot()
  const { itineraryData, multiCartId } = useItineraryContext()
  useIsomorphicLayoutEffect(reset, [isLoggedIn, itineraryData?.tripChatId, reset])
  const currentCurrency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const projection = useCartPanelItems({
    itineraryData,
    multiCartId,
    isLoggedIn,
    optimisticExclusions,
    refreshFailedFlightKeys,
  })
  const { items, isLoading, isError, refetch, refetchCartItems } = projection
  const { chatId } = useChatStreamStatus()
  const updateCartInclusion = useCartInclusionMutation(chatId, setOptimisticExclusions, refetchCartItems)
  const contextValue = useMemo(() => ({ items, isLoading, isError, refetch, refetchCartItems, updateCartInclusion, setRefreshFailedFlightKeys }), [items, isLoading, isError, refetch, refetchCartItems, updateCartInclusion])
  const cartItemPrices = useMemo(() => buildCartItemAssistantPrices(items, {
    currentCurrency, currentRegionCode, hasLuxPlusBenefits,
  }), [currentCurrency, currentRegionCode, hasLuxPlusBenefits, items])

  const summary = useMemo<CartContextSummary>(() => {
    const hasMissingPrice = items.some(item => !item.cartExcluded && getCartItemDisplayPrice(item.pricing, hasLuxPlusBenefits) === undefined)
    const status = isError || items.some(item => item.pricing.error) ? 'error' : !itineraryData || isLoading ? 'loading' : hasMissingPrice ? 'partial' : 'ready'
    const { totalPrice } = computeCartPanelTotal(items.map(item => ({
      cartExcluded: item.cartExcluded,
      includeInTotal: shouldIncludeCartPanelItemPriceEstimate(item),
      price: item.pricing.price,
      memberPrice: item.pricing.memberPrice,
    })), hasLuxPlusBenefits)
    return {
      status,
      estimatedTotal: status === 'ready' ? formatCurrency(totalPrice, currentCurrency, currentRegionCode) : undefined,
      readyItemCount: items.filter(isCartPanelItemCheckoutReady).length,
    }
  }, [currentCurrency, currentRegionCode, hasLuxPlusBenefits, isError, isLoading, items, itineraryData])

  useIsomorphicLayoutEffect(() => {
    if (!itineraryData || isLoading || isError) {
      snapshotRef.current.selectedFlightEntries = undefined
      return
    }
    const entries = new Map<string, TripPlannerCommittedStateEntry>()
    for (const item of items) {
      if (item.productType !== 'flight') continue
      for (const leg of item.multiCityLegs ?? [item]) {
        if (!leg.flightCartItem || !isFlightItem(leg.flightCartItem.item)) {
          snapshotRef.current.selectedFlightEntries = undefined
          return
        }
        const transport = findTransportConnectionForOption(itineraryData, leg.option.itemId)
        if (!transport) continue
        const entry = buildRefreshedFlightItineraryCommittedStateEntry(transport, leg.flightCartItem.item)
        if (entry) entries.set(entry.key, entry)
      }
    }
    snapshotRef.current.selectedFlightEntries = entries
    return () => { snapshotRef.current.selectedFlightEntries = undefined }
  }, [isError, isLoading, items, itineraryData, snapshotRef])

  return <CartProjectionContext.Provider value={contextValue}>
    <CartPricingSnapshotRegistration cartItemPrices={cartItemPrices} summary={summary} />
    {children}
  </CartProjectionContext.Provider>
}
